/**
 * DetailHero — school identity block + action rail.
 *
 * Mobile: identity stacked, action buttons in a 2-up row at the bottom.
 * Desktop: identity left, vertical action stack on the right.
 *
 * Matches detail-frames.jsx DetailHero + IdentityBlock + ActionGroup.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import type { SchoolDetail, CoachDetail } from '../../api/school';
import { DivBadgeLg } from './DivBadgeLg';
import { ActionButton } from './ActionButton';
import { IconBookmark, IconMail, IconExternal } from './icons';
import { scrollToCoaches } from '../../utils/scroll';

interface Props {
  detail: SchoolDetail;
  sport: string;
  isAuthenticated: boolean;
  isSaved: boolean;
  onWatchlistToggle: () => void;
  backLinkHref: string;
  backLinkLabel: string;
  coaches: CoachDetail[];
}

/** Convert sport slug to display label, e.g. "soccer_mens" → "Men's Soccer". */
function sportLabel(sportSlug: string): string {
  if (sportSlug === 'soccer_mens') return "Men's Soccer";
  if (sportSlug === 'soccer_womens') return "Women's Soccer";
  if (sportSlug === 'football_mens') return 'Football';
  return sportSlug.replace(/_/g, ' ');
}

/** Convert sport slug to human-readable label for aria-labels, e.g. "soccer_mens" → "men's soccer". */
function sportDisplayName(sportSlug: string): string {
  if (sportSlug === 'soccer_mens') return "men's soccer";
  if (sportSlug === 'soccer_womens') return "women's soccer";
  if (sportSlug === 'football_mens') return 'football';
  return sportSlug.replace(/_/g, ' ');
}


export function DetailHero({
  detail,
  sport,
  isAuthenticated,
  isSaved,
  onWatchlistToggle,
  backLinkHref,
  backLinkLabel,
  coaches,
}: Props) {
  const saveLabel = !isAuthenticated
    ? 'Save to watchlist'
    : isSaved
    ? 'Saved'
    : 'Save to watchlist';

  const saveSub = !isAuthenticated
    ? 'Sign in to track this program'
    : isSaved
    ? 'Click to remove'
    : 'Track open spots over time';

  // Derive coach button state from coaches prop.
  const hasAnyCoach = coaches.length > 0;
  const hasEmail = coaches.some((c) => c.email != null && c.email.trim() !== '');
  const coachButtonDisabled = !hasAnyCoach;
  const coachButtonSub = !hasAnyCoach
    ? 'No coaches on record'
    : !hasEmail
    ? 'No email listed'
    : coaches.length === 1
    ? coaches[0]!.name
    : `${coaches.length} coaches`;

  return (
    <section
      aria-labelledby="school-heading"
      className="bg-surface-card border border-hairline rounded-xl p-4 md:p-6"
    >
      {/* Back link — uses ← for textContent compatibility with existing tests */}
      <Link
        to={backLinkHref}
        aria-label={backLinkLabel}
        className="min-h-[44px] inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 mb-3 md:mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
      >
        <span aria-hidden="true">←</span>
        {backLinkLabel}
      </Link>

      {/* Main row: identity + actions */}
      <div className="flex flex-wrap md:flex-nowrap gap-4 md:gap-5 items-start">
        {/* Identity block */}
        <div className="flex-1 min-w-0">
          <h1
            id="school-heading"
            className="text-[22px] md:text-[30px] font-bold tracking-tight text-gray-900 leading-tight"
          >
            {detail.school_name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-gray-600">
            {/* Use division_name for human-readable display (e.g. "NCAA Division I") */}
            <DivBadgeLg>{detail.division_name}</DivBadgeLg>
            <span>{detail.region} · {detail.state}</span>
            <span className="text-gray-400" aria-hidden="true">·</span>
            <span className="text-gray-700">{sportLabel(sport)}</span>
            {detail.conference && (
              <>
                <span className="text-gray-400" aria-hidden="true">·</span>
                <span>{detail.conference}</span>
              </>
            )}
          </div>
          {detail.roster_source_url !== null && (
            <div className="mt-2">
              <a
                href={detail.roster_source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] text-brand-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
                aria-label={`View ${detail.school_name} ${sportDisplayName(sport)} roster on the athletics website (opens in new tab)`}
              >
                View source roster
                <IconExternal size={11} />
              </a>
            </div>
          )}
        </div>

        {/* Desktop: vertical action stack */}
        <div className="hidden md:flex flex-col gap-2 w-[220px] flex-shrink-0">
          <ActionButton
            icon={IconBookmark}
            label={saveLabel}
            sub={saveSub}
            primary
            full
            onClick={onWatchlistToggle}
            ariaLabel={saveLabel}
          />
          <ActionButton
            icon={IconMail}
            label="Coach contact"
            sub={coachButtonSub}
            full
            disabled={coachButtonDisabled}
            onClick={coachButtonDisabled ? undefined : scrollToCoaches}
            ariaLabel={`View coach contact info for ${detail.school_name}`}
          />
        </div>
      </div>

      {/* Mobile: 2-up action row (shown below identity) */}
      <div className="flex md:hidden gap-2 mt-4">
        <div className="flex-1">
          <ActionButton
            icon={IconBookmark}
            label="Save"
            primary
            full
            onClick={onWatchlistToggle}
            ariaLabel={saveLabel}
          />
        </div>
        <div className="flex-1">
          <ActionButton
            icon={IconMail}
            label="Coach contact"
            sub={coachButtonSub}
            full
            disabled={coachButtonDisabled}
            onClick={coachButtonDisabled ? undefined : scrollToCoaches}
            ariaLabel={`View coach contact info for ${detail.school_name}`}
          />
        </div>
      </div>
    </section>
  );
}
